import type {
  AutoscaleInfo,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/**
 * Stand-in for lightweight-charts' `createSeriesMarkers` plugin that takes part
 * in the shared marker layout: the plugin stacks its own markers off the bar
 * extremes and knows nothing of the other primitives, so its BOS / CHoCH arrows
 * and the other-symbol tags landed on top of them.
 *
 * Same `setMarkers(SeriesMarker[])` input and the same shapes and sizes as the
 * plugin (arrowUp / arrowDown / circle / square, size from the bar spacing,
 * text past the shape; an unknown shape draws its text only, as the plugin
 * does). aboveBar / belowBar markers become one layout box each (shape + text);
 * the layout decides how far out they sit. Also reports the plugin's autoscale
 * margins so the price scale keeps the same headroom.
 */
export interface LayoutSeriesMarker {
  time: Time;
  position: 'aboveBar' | 'belowBar' | 'inBar';
  color: string;
  shape: string;
  size?: number;
  text?: string;
  id?: string;
}

const MIN_SHAPE = 12;
const MAX_SHAPE = 30;
const TEXT_PX = 12;
const TEXT_FONT = `${TEXT_PX}px -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif`;

function ceiledOdd(n: number): number {
  const c = Math.ceil(n);
  return c % 2 === 0 ? c - 1 : c;
}
function ceiledEven(n: number): number {
  const c = Math.ceil(n);
  return c % 2 !== 0 ? c - 1 : c;
}
function sizeFor(barSpacing: number, coeff: number): number {
  return ceiledOdd(Math.min(Math.max(barSpacing, MIN_SHAPE), MAX_SHAPE) * coeff);
}
/** The plugin's shape height for a bar spacing. */
export function markerShapeHeight(barSpacing: number): number {
  return ceiledEven(sizeFor(barSpacing, 1));
}
function shapeMargin(barSpacing: number): number {
  return Math.max(sizeFor(barSpacing, 0.1), 3);
}
const KNOWN_SHAPES = new Set(['arrowUp', 'arrowDown', 'circle', 'square']);

interface Ref {
  m: LayoutSeriesMarker;
  /** Shape box side (0 for text only), px. */
  shape: number;
  textW: number;
}

interface Drawn {
  x: number;
  /** Box top / height. */
  top: number;
  h: number;
  ref: Ref;
  above: boolean;
}

class Renderer implements IPrimitivePaneRenderer {
  private _items: Drawn[] = [];
  update(items: Drawn[]): void {
    this._items = items;
  }
  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = TEXT_FONT.replace(`${TEXT_PX}px`, `${TEXT_PX * vr}px`);
      for (const d of this._items) {
        const { m, shape } = d.ref;
        ctx.fillStyle = m.color;
        // Shape nearest the candle, text beyond it (as the plugin draws them).
        const hasText = !!m.text;
        const shapeTop = d.above ? d.top + d.h - shape : d.top;
        const cx = d.x * hr;
        const cy = (shapeTop + shape / 2) * vr;
        const half = (shape / 2) * Math.min(hr, vr);
        if (shape > 0) {
          ctx.beginPath();
          switch (m.shape) {
            case 'circle':
              ctx.arc(cx, cy, half * 0.8, 0, Math.PI * 2);
              break;
            case 'square':
              ctx.rect(cx - half * 0.7, cy - half * 0.7, half * 1.4, half * 1.4);
              break;
            case 'arrowUp':
            case 'arrowDown': {
              const up = m.shape === 'arrowUp';
              const dir = up ? -1 : 1;
              const base = half * 0.5;
              // Head on the outward half, shaft on the inner half.
              ctx.moveTo(cx - half, cy);
              ctx.lineTo(cx, cy + dir * half);
              ctx.lineTo(cx + half, cy);
              ctx.lineTo(cx + base, cy);
              ctx.lineTo(cx + base, cy - dir * half);
              ctx.lineTo(cx - base, cy - dir * half);
              ctx.lineTo(cx - base, cy);
              break;
            }
          }
          ctx.closePath();
          ctx.fill();
        }
        if (hasText) {
          const ty = d.above ? shapeTop - TEXT_PX * 0.6 : shapeTop + shape + TEXT_PX * 0.6;
          ctx.fillText(m.text!, cx, ty * vr);
        }
      }
      ctx.restore();
    });
  }
}

class PaneView implements IPrimitivePaneView {
  private _renderer = new Renderer();
  constructor(private _source: LayoutSeriesMarkersPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.drawn());
    return this._renderer;
  }
}

export class LayoutSeriesMarkersPrimitive implements ISeriesPrimitive<Time> {
  /** Named like the plugin's field so headless probes count them the same way. */
  _markers: LayoutSeriesMarker[] = [];
  private _view = new PaneView(this);
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _layout = new MarkerLayoutClient('seriesMarkers', (ctx) => this._layoutItems(ctx));

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }

  updateAllViews(): void {
    /* positions come from the layout at render time */
  }

  /** Same contract as the plugin's setMarkers: markers ascending by time. */
  setMarkers(markers: LayoutSeriesMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  markers(): readonly LayoutSeriesMarker[] {
    return this._markers;
  }

  autoscaleInfo(): AutoscaleInfo | null {
    if (this._markers.length === 0 || !this._chart) return null;
    const bs = (this._chart.timeScale().options() as { barSpacing?: number }).barSpacing ?? 6;
    const margin = markerShapeHeight(bs) * 1.5 + shapeMargin(bs) * 2;
    let above = false;
    let below = false;
    for (const m of this._markers) {
      if (m.position === 'aboveBar') above = true;
      else if (m.position === 'belowBar') below = true;
      if (above && below) break;
    }
    return { priceRange: null, margins: { above: above ? margin : 0, below: below ? margin : 0 } } as unknown as AutoscaleInfo;
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    const shapeH = markerShapeHeight(ctx.barSpacing);
    const gap = shapeMargin(ctx.barSpacing);
    for (let i = 0; i < this._markers.length; i += 1) {
      const m = this._markers[i];
      const t = m.time as number;
      if (typeof t !== 'number' || t < ctx.fromTime || t > ctx.toTime) continue;
      const x = ctx.timeToX(t);
      const bar = ctx.bar(t);
      if (x === null || !bar) continue;
      const above = m.position !== 'belowBar';
      const anchorY = ctx.priceToY(m.position === 'inBar' ? bar.close : above ? bar.high : bar.low);
      if (anchorY === null) continue;
      const shape = KNOWN_SHAPES.has(m.shape) ? Math.max(1, Math.round(shapeH * (m.size ?? 1))) : 0;
      const textW = m.text ? ctx.measure(m.text, TEXT_FONT) : 0;
      const textH = m.text ? Math.ceil(TEXT_PX * 1.2) : 0;
      const w = Math.max(shape, textW, 1);
      const h = shape + textH;
      if (h <= 0) continue;
      const ref: Ref = { m, shape, textW };
      out.push({
        key: m.id ?? `${t}:${m.position}:${m.shape}:${m.text ?? ''}:${i}`,
        time: t,
        x,
        side: above ? 'above' : 'below',
        anchorY,
        w,
        h,
        gap,
        priority: LAYOUT_PRIORITY.seriesMarker,
        color: m.color,
        label: m.text ? `${m.text}` : m.shape,
        ref,
      });
    }
    return out;
  }

  /** What the renderer draws this frame. */
  drawn(): Drawn[] {
    if (!this._series || !this._chart) return [];
    const placed = this._layout.placed();
    if (placed) {
      return placed.map((p) => ({ x: p.item.x, top: p.top, h: p.h, ref: p.item.ref as Ref, above: p.item.side === 'above' }));
    }
    // No registry (tests, other charts): the plugin's own stacking.
    const ts = this._chart.timeScale();
    const bs = (ts.options() as { barSpacing?: number }).barSpacing ?? 6;
    const shapeH = markerShapeHeight(bs);
    const gap = shapeMargin(bs);
    const offsets = new Map<string, number>();
    const out: Drawn[] = [];
    for (const m of this._markers) {
      const x = ts.timeToCoordinate(m.time);
      const idx = ts.timeToIndex(m.time, false);
      if (x === null || idx === null) continue;
      const bar = this._series.dataByIndex(idx as unknown as number) as { high?: number; low?: number; close?: number } | null;
      if (!bar || typeof bar.high !== 'number' || typeof bar.low !== 'number') continue;
      const above = m.position !== 'belowBar';
      const y = this._series.priceToCoordinate(m.position === 'inBar' ? (bar.close as number) : above ? bar.high : bar.low);
      if (y === null) continue;
      const shape = KNOWN_SHAPES.has(m.shape) ? Math.round(shapeH * (m.size ?? 1)) : 0;
      const h = shape + (m.text ? Math.ceil(TEXT_PX * 1.2) : 0);
      const k = `${m.time}:${above}`;
      const off = offsets.get(k) ?? 0;
      offsets.set(k, off + h + gap);
      const top = above ? y - gap - off - h : y + gap + off;
      out.push({ x, top, h, ref: { m, shape, textW: 0 }, above });
    }
    return out;
  }
}
